import { Smartphone, Apple, Bell } from "lucide-react";
import { APP_COMING_SOON } from "../data/mock";
import { useInView } from "../hooks/useInView";

export const AppComingSoon = () => {
  const [ref, isInView] = useInView();
  return (
    <section className="py-16 lg:py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-sky-950 text-white relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-lime-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center" ref={ref}>
          <div className={`transition-all duration-700 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-lime-500/15 border border-lime-400/30 text-lime-300 text-xs font-bold uppercase tracking-wider mb-5">
              <Bell className="w-3 h-3" /> Coming Soon
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-5">
              {APP_COMING_SOON.headline}
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              {APP_COMING_SOON.subline}
            </p>

            <ul className="space-y-3 mb-8">
              {[
                "Book pickup in 30 seconds",
                "Live order tracking",
                "Photo-verified deliveries",
                "One-tap re-order",
              ].map((f, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-lime-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="w-1.5 h-1.5 bg-lime-400 rounded-full" />
                  </div>
                  <span className="text-slate-200 text-sm">{f}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                disabled
                className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-white/5 border border-white/10 cursor-not-allowed opacity-70"
                data-testid="app-ios-btn"
              >
                <Apple className="w-6 h-6" />
                <div className="text-left">
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Coming soon on</p>
                  <p className="text-sm font-bold">App Store</p>
                </div>
              </button>
              <button
                type="button"
                disabled
                className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-white/5 border border-white/10 cursor-not-allowed opacity-70"
                data-testid="app-android-btn"
              >
                <Smartphone className="w-6 h-6" />
                <div className="text-left">
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Coming soon on</p>
                  <p className="text-sm font-bold">Google Play</p>
                </div>
              </button>
            </div>
          </div>

          {/* Phone mockup */}
          <div className={`relative flex justify-center transition-all duration-700 delay-200 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative w-[260px] h-[520px] rounded-[42px] bg-slate-950 border-[10px] border-slate-900 shadow-2xl shadow-sky-500/20 overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-slate-900 rounded-b-2xl z-10" />
              <div className="h-full bg-gradient-to-br from-sky-500 via-sky-600 to-sky-800 p-6 pt-14 text-white flex flex-col">
                <div className="text-[10px] uppercase tracking-widest opacity-80">Blitzwash</div>
                <h3 className="text-2xl font-extrabold mt-1 leading-tight">Fresh clothes, one tap away.</h3>
                <div className="mt-6 space-y-3">
                  {["Wash & Fold", "Wash & Steam Iron", "Dry Cleaning"].map((s, i) => (
                    <div key={i} className="bg-white/15 backdrop-blur rounded-2xl p-3 flex items-center justify-between">
                      <span className="text-sm font-medium">{s}</span>
                      <span className="text-[10px] bg-lime-400/90 text-slate-900 px-2 py-0.5 rounded-full font-bold">Book</span>
                    </div>
                  ))}
                </div>
                <div className="mt-auto text-center">
                  <div className="inline-block px-3 py-1 rounded-full bg-white/20 text-[10px] font-semibold">
                    24-hour turnaround
                  </div>
                </div>
              </div>
            </div>
            {/* Floating badges */}
            <div className="absolute -left-2 top-16 bg-white rounded-2xl shadow-xl p-3 flex items-center gap-2 animate-float">
              <div className="w-8 h-8 rounded-full bg-lime-500/20 flex items-center justify-center">
                <span className="text-xs font-bold text-lime-600">✓</span>
              </div>
              <div>
                <p className="text-[10px] text-slate-500">Order Delivered</p>
                <p className="text-xs font-bold text-slate-900">In 24h</p>
              </div>
            </div>
            <div className="absolute -right-2 bottom-24 bg-white rounded-2xl shadow-xl p-3 flex items-center gap-2 animate-float" style={{animationDelay:"1s"}}>
              <span className="text-lg">⭐</span>
              <div>
                <p className="text-[10px] text-slate-500">Rated</p>
                <p className="text-xs font-bold text-slate-900">4.9 / 5</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
